import { FieldTypes } from "pages/employer-of-record/components/AutoForm";
import { countryIs, field, personalInfo, REQUIRED } from "./utils";
import * as KENYA from "pages/employer-of-record/onboarding/profile-info/form/ke/constants.ke";

const KRA_PIN_REGEX = /^[A-Za-z]\d{9}[A-Za-z]$/;

export const kenyaFields = {
  keGender: field({
    type: FieldTypes.select,
    informationType: "identification",
    name: "keGender",
    label: "Gender",
    placeholder: "Select gender",
    options: KENYA.GENDER_OPTIONS,
    validations: REQUIRED,
    scenarios: {
      onboarding: countryIs("KE"),
    },
    value: personalInfo("keGender", ""),
  }),
  keKraPin: field({
    type: FieldTypes.text,
    informationType: "taxId",
    name: "keKraPin",
    label: "KRA PIN (Kenya Revenue Authority)",
    validations: {
      ...REQUIRED,
      text: {
        fn: {
          value: (value) =>
            !value || KRA_PIN_REGEX.test(String(value).toUpperCase()),
          message:
            "Must be 11 characters: 1 letter, 9 digits, 1 letter (e.g. A123456789B)",
        },
      },
    },
    scenarios: {
      onboarding: countryIs("KE"),
    },
    value: personalInfo("keKraPin", ""),
    serialize: (value) => (value ? String(value).toUpperCase() : value),
  }),
  keIdentityType: field({
    type: FieldTypes.select,
    informationType: "identification",
    name: "keIdentityType",
    label: "Identity Type",
    placeholder: "Select identity type",
    options: KENYA.IDENTITY_TYPE_OPTIONS,
    validations: REQUIRED,
    scenarios: {
      onboarding: countryIs("KE"),
    },
    value: personalInfo("keIdentityType", ""),
  }),
  keNationalId: field({
    type: FieldTypes.text,
    informationType: "identification",
    name: "keNationalId",
    label: "National ID",
    validations: {
      required: {
        when: {
          keIdentityType: (value) => value === "national_id",
        },
      },
      text: {
        fn: {
          value: (value) => !value || /^\d+$/.test(String(value)),
          message: "Must contain digits only",
        },
      },
    },
    hideWhen: {
      keIdentityType: (value) => value !== "national_id",
    },
    scenarios: {
      onboarding: countryIs("KE"),
    },
    value: personalInfo("keNationalId", ""),
  }),
  keForeignId: field({
    type: FieldTypes.text,
    informationType: "identification",
    name: "keForeignId",
    label: "Foreign ID",
    validations: {
      required: {
        when: {
          keIdentityType: (value) => value === "foreign_id",
        },
      },
    },
    hideWhen: {
      keIdentityType: (value) => value !== "foreign_id",
    },
    scenarios: {
      onboarding: countryIs("KE"),
    },
    value: personalInfo("keForeignId", ""),
  }),
  keTaxStatus: field({
    type: FieldTypes.select,
    informationType: "identification",
    name: "keTaxStatus",
    label: "Tax Status",
    placeholder: "Select tax status",
    message:
      "Select your tax status. Note that most standard employees would be a status of resident & deemed resident",
    options: KENYA.TAX_STATUS_OPTIONS,
    validations: REQUIRED,
    scenarios: {
      onboarding: countryIs("KE"),
    },
    value: personalInfo("keTaxStatus", ""),
  }),
  keNssfNumber: field({
    type: FieldTypes.text,
    informationType: "identification",
    name: "keNssfNumber",
    label: "NSSF Number (National Social Security Fund)",
    validations: {
      required: {
        when: {
          keTaxStatus: (value) => value !== "apprentice_no_nssf",
        },
      },
      text: {
        fn: {
          value: (value) => !value || /^\d{9,10}$/.test(String(value)),
          message: "Must be 9 to 10 digits",
        },
      },
    },
    scenarios: {
      onboarding: countryIs("KE"),
    },
    value: personalInfo("keNssfNumber", ""),
  }),
  keShifNumber: field({
    type: FieldTypes.text,
    informationType: "identification",
    name: "keShifNumber",
    label: "SHIF Number (Social Health Insurance Fund)",
    validations: {
      ...REQUIRED,
      text: {
        fn: {
          value: (value) => !value || /^\d+$/.test(String(value)),
          message: "Must contain digits only",
        },
      },
    },
    scenarios: {
      onboarding: countryIs("KE"),
    },
    value: personalInfo("keShifNumber", ""),
  }),
  keDisabledExemptionCertNumber: field({
    type: FieldTypes.text,
    informationType: "identification",
    name: "keDisabledExemptionCertNumber",
    label: "Disabled Exemption Certificate Number",
    validations: {
      required: {
        when: {
          keTaxStatus: (value) => value === "disability_tax_exemption",
        },
      },
    },
    hideWhen: {
      keTaxStatus: (value) => value !== "disability_tax_exemption",
    },
    scenarios: {
      onboarding: countryIs("KE"),
    },
    value: personalInfo("keDisabledExemptionCertNumber", ""),
  }),
};
